// ============================================================
// drive_control.js
// EV Powertrain Simulator — core 모듈
// 구동 제어 — 드라이브 모드, 회생제동 맵, 발진 제어
// Extracted from ev-sim_v1_0_Verification_46.jsx (Phase 2)
// ============================================================

import { ROAD_MU, G_GRAVITY }       from '../../constants/physics_consts.js';
import { DRIVE_MODE_REGEN_DEFAULT } from '../control/drive_mode_config.js';

// [Fix-DC1] math_utils.js 의존 함수 명시적 import — regenMapDemand·driveModeThrottle bilinear2D/lerp1D 누락 수정
import { bilinear2D, lerp1D } from './math_utils.js';

// [Fix-DC1] battery.js 의존 함수 명시적 import — regenTorqueEnhanced·computeRegenChain 경로
import { P_chg_max_T, R_int_T } from './battery.js';

// [Fix-DC1] motor.js 의존 함수 명시적 import — regenTorqueEnhanced·computeRegenChain 경로
import { T_max_motor_T, Rs_T, lambda_pm_T } from './motor.js';


// ── driveModeThrottle ──
/**
 * 드라이브 모드와 페달 입력으로 실효 스로틀 비율을 계산한다.
 *
 * 동작:
 *   1) APP(가속 페달) 데드존 필터링: pedal ≤ dz → 0, 이상은 [0,1]로 선형 재매핑
 *   2) traction_lut_enable=true 면 모드별 2D LUT(APP% × 속도)를 bilinear2D 보간 → 토크 → throttle
 *   3) LUT 미사용 시 Power-function 폴백:
 *      throttle = (p_eff ^ (1/sens)) × maxRatio
 *      ECO: sens=0.6·max=0.70 / SPORT: sens=1.0·max=1.15 / SNOW: sens=0.4·max=0.55
 *
 * @param {number} pedal_pos  가속 페달 위치 [0, 1]
 * @param {object} params     차량 파라미터
 * @param {number} v_kmh      현재 차속 [km/h]
 * @returns {number}          실효 스로틀 비율 [0, maxRatio+0.05]
 */
function driveModeThrottle(pedal_pos, params, v_kmh) {
  // ── [A17] APP 데드존 필터링: pedal ≤ dz → 0, 이상은 선형 재매핑 ──────────
  // 수식: p_eff = clip((pedal - dz) / (1 - dz), 0, 1)
  const dz = Math.min(0.45, (params.app_deadzone_pct || 0) / 100);
  const p_eff = pedal_pos <= dz ? 0 : Math.min(1, (pedal_pos - dz) / Math.max(0.01, 1 - dz));
  const mode = params.drive_mode || 'NORMAL';
  // ── LUT 모드: 2D 쌍선형 보간 (N·m → throttle ratio) ──────
  if (params.traction_lut_enable) {
    const V_PTS   = [0, 20, 40, 60, 80, 100, 120, 140];
    const APP_PTS = [0, 10, 25, 50, 75, 100];
    const app_pct = Math.min(100, Math.max(0, p_eff * 100));  // [A17] deadzone 적용 후 페달%
    const v = Math.max(0, v_kmh || 0);
    const lutKey = { ECO:'traction_lut_eco', NORMAL:'traction_lut_normal', SPORT:'traction_lut_sport', SNOW:'traction_lut_snow', OFF_ROAD:'traction_lut_offroad' }[mode] || 'traction_lut_normal';
    const lut = params[lutKey];
    if (lut && lut.length >= 2) {
      // LUT 값은 N·m (목표 토크). throttle = T_req / T_max_motor
      const T_req_Nm = bilinear2D(lut, APP_PTS, V_PTS, app_pct, v);
      const T_max    = params.T_max_motor || 400;
      // [BugFix-A02-01] 클램프 상한을 모드별 maxRatio로 확장 (1.0 고정 제거)
      const modeMaxRatio = { ECO: params.eco_max_torque_ratio||0.7,
                             NORMAL: params.normal_max_torque_ratio||1.0,
                             SPORT: params.sport_max_torque_ratio||1.15,
                             SNOW: params.snow_max_torque_ratio||0.55,
                             OFF_ROAD: params.offroad_max_torque_ratio||1.0 }[mode] || 1.0;
      return Math.max(0, Math.min(modeMaxRatio + 0.05, T_req_Nm / T_max));
    }
  }
  // ── Power-function 폴백 ────────────────────────────────────
  let sens, maxRatio;
  if (mode === 'ECO') {
    sens = params.eco_pedal_sensitivity || 0.6;
    maxRatio = params.eco_max_torque_ratio || 0.7;
  } else if (mode === 'SPORT') {
    sens = params.sport_pedal_sensitivity || 1.0;
    maxRatio = params.sport_max_torque_ratio || 1.15;
  } else if (mode === 'SNOW') {
    sens = params.snow_pedal_sensitivity || 0.4;
    maxRatio = params.snow_max_torque_ratio || 0.55;
  } else if (mode === 'OFF_ROAD') {
    sens = params.offroad_pedal_sensitivity || 0.75;
    maxRatio = params.offroad_max_torque_ratio || 1.0;
  } else {
    sens = params.normal_pedal_sensitivity || 0.85;
    maxRatio = params.normal_max_torque_ratio || 1.0;
  }
  // Sensitivity curve: power function makes response feel linear or nonlinear
  const exponent = 1.0 / Math.max(0.1, sens);
  const throttle_effective = Math.pow(Math.max(0, Math.min(1, p_eff)), exponent);  // [A17] p_eff
  // [BugFix-A02-01] 클램프 상한을 maxRatio+0.05로 확장 (SPORT 1.15, OFF_ROAD 1.0 실효화)
  return Math.min(maxRatio + 0.05, throttle_effective * maxRatio);
}

// ── driveModeRegenStrength — SST: ../control/drive_mode_config.js ──
/**
 * 현재 드라이브 모드의 회생제동 강도 기준값을 반환한다.
 *
 * 실제 값은 drive_mode_config.js의 DRIVE_MODE_REGEN_DEFAULT 함수 테이블에서 계산된다.
 * ECO: 0.5 / NORMAL: 0.7 / SPORT: 1.0 / SNOW: 0.3 / OFF_ROAD: 0.6 (기본값)
 * params.eco_regen_strength 등 사용자 파라미터로 개별 조정 가능.
 *
 * @param {object} params  차량 파라미터 (drive_mode, *_regen_strength 포함)
 * @returns {number}       회생 강도 [0, 1]
 */
function driveModeRegenStrength(params) {
  const mode = (params.drive_mode || 'NORMAL').toUpperCase();
  const fn = DRIVE_MODE_REGEN_DEFAULT[mode] || DRIVE_MODE_REGEN_DEFAULT.NORMAL;
  return fn(params);
}

// ── regenMapDemand ──
/**
 * 차속·페달 위치로부터 목표 회생제동 수요(demand)를 계산한다.
 *
 * 동작 경로:
 *   · regen_calib_enable=true: 캘리브레이션 LUT(1D·앱 기반)로 정밀 맵핑
 *   · 폴백: one-pedal 모드(pedal<0.05 → 강회생), 일반 코스팅 회생
 *   고속 구간(blend_start_kph 이상)에서는 blend_ratio로 회생을 선형 감소시킨다.
 *
 * @param {number} v_ms      현재 차속 [m/s]
 * @param {number} pedal_pos 가속 페달 위치 [0, 1]
 * @param {object} params    차량 파라미터
 * @returns {number}         회생제동 수요 [0, 1]
 */
function regenMapDemand(v_ms, pedal_pos, params) {
  const one_pedal = params.one_pedal_enable;
  const v_kmh = v_ms * 3.6;
  const regen_strength = driveModeRegenStrength(params);

  // ── 캘리브레이션 LUT 모드 ──────────────────────────────────
  if (params.regen_calib_enable) {
    const app_pct = pedal_pos * 100;
    let demand = 0;
    if (one_pedal && app_pct < 8) {
      // One-pedal 1D LUT
      demand = lerp1D(params.regen_onepedal_app_pts || [0,5,8], params.regen_onepedal_str || [1,0.1,0], app_pct) * regen_strength;
    } else if (app_pct < 1) {
      // Coasting regen 1D LUT
      demand = lerp1D(params.regen_coast_v_pts || [0,15,150], params.regen_coast_str || [0,0.4,0.8], v_kmh) * regen_strength;
    }
    const blend_start = params.regen_blend_start_kph || 80;
    const blend_ratio = v_kmh > blend_start ? Math.max(0.3, 1 - (v_kmh - blend_start) / 120) : 1.0;
    return Math.max(0, Math.min(1, demand * blend_ratio));
  }

  // Speed fade: below regen_low_speed_fade_kph, regen tapers off
  const v_fade_kph = params.regen_low_speed_fade_kph || 15;
  const speed_fade = Math.min(1, v_kmh / Math.max(1, v_fade_kph));

  let demand = 0;
  if (one_pedal && pedal_pos < 0.05) {
    // One-pedal: 0% pedal → strong regen
    demand = (params.regen_one_pedal_level || 0.4) * regen_strength;
  } else if (!one_pedal && pedal_pos < 0.01) {
    // Normal coast (near-zero pedal, no one-pedal)
    demand = 0;
  }
  // High-speed blending: reduce regen ratio above blend_start_kph
  const blend_start = params.regen_blend_start_kph || 80;
  const blend_ratio = v_kmh > blend_start
    ? Math.max(params.regen_blend_ratio_high || 0.7, 1 - (v_kmh - blend_start) / 100)
    : 1.0;

  return Math.max(0, Math.min(1, demand * speed_fade * blend_ratio));
}

// ── regenTorqueEnhanced ──
/**
 * 물리 한계를 반영한 실효 회생제동 토크와 마찰 토크를 계산한다.
 *
 * 적용 한계 (가장 작은 값이 상한):
 *   · 배터리 충전 전력 한계 P_chg_max (온도 + SOC 의존)
 *   · DC-링크 과전압 한계 (V_max_dc=V_dc×1.15 기준 역산)
 *   · 모터 온도 디레이팅 한계 T_max_motor_T
 *   · 트랙션 후축 마찰 한계 μ×m×g×rear_weight_bias
 *   · 저속 fade-out (regen_blend_low_speed 미만)
 *
 * friction_calib_enable=true 시 EBD·ABS 분배 LUT를 사용해 전/후 제동 비율을 정밀 계산한다.
 *
 * @param {number} omega_motor   모터 각속도 [rad/s]
 * @param {number} brake_demand  제동 수요 [0, 1] (1 = 풀 제동)
 * @param {object} params        차량 파라미터
 * @param {number} soc           현재 SOC [0, 1]
 * @param {number} T_batt        배터리 온도 [°C]
 * @param {number} T_motor       모터 온도 [°C]
 * @param {number} T_inv         인버터 온도 [°C]
 * @param {number} [v_kmh_in]    현재 차속 [km/h] (EBD LUT 보간용, 선택)
 * @returns {{ T_regen, T_friction, P_regen, T_regen_max, P_regen_limit, speed_fade, ... }}
 */
function regenTorqueEnhanced(omega_motor, brake_demand, params, soc, T_batt, T_motor, T_inv, v_kmh_in) {
  if (omega_motor < 1) return { T_regen: 0, T_friction: brake_demand > 0 ? brake_demand * params.T_max_motor * 0.5 / params.r_wheel : 0, P_regen: 0 };
  // Battery charge limit (thermal + SOC)
  const P_chg_max = P_chg_max_T(T_batt || 25, soc, params);
  // Motor derate
  const T_max_m = (T_motor !== undefined) ? T_max_motor_T(T_motor, params) : params.T_max_motor;
  const T_regen_motor_max = T_max_m * 0.5;
  // Traction limit (rear axle)
  const mu_eff = ROAD_MU[params.road_condition] || params.mu;
  // [Fix-RearWeight] 후축 하중 비율 하드코딩(0.6) → 동적 파라미터
  //   params.rear_weight_bias  : 직접 지정 (예: RWD 0.55, AWD 0.50, FWD 0.40)
  //   params.front_weight_bias : 전축 비율로부터 역산 (없으면 0.4 가정)
  //   미지정 시 0.6 유지(하위 호환) — AWD/FWD 차종에서 과대 회생 제동 방지
  const rear_weight_bias = params.rear_weight_bias ?? (1 - (params.front_weight_bias ?? 0.4));
  const F_trac_max = mu_eff * params.m * G_GRAVITY * rear_weight_bias;
  const T_regen_traction = F_trac_max * params.r_wheel / params.gear_ratio;
  // DC-link overvoltage limit
  const V_oc = params.V_dc * 1.05;
  const V_max_dc = params.V_dc * 1.15;
  const Ri = R_int_T(soc, T_batt || 25, params);
  const I_chg_max_voltage = Math.max(0, (V_max_dc - V_oc) / Ri);
  const P_regen_voltage = I_chg_max_voltage * V_oc;
  // Combined limit
  const P_regen_limit = Math.min(P_chg_max, P_regen_voltage);
  const T_regen_power = P_regen_limit / Math.max(omega_motor, 1);
  // Speed fade-out
  const speed_fade = Math.min(1, omega_motor / Math.max(params.regen_blend_low_speed, 1));
  const T_regen_max = Math.min(T_regen_motor_max, T_regen_power, T_regen_traction) * speed_fade;

  // ── 캘리브레이션 Friction Brake 분배 LUT ──────────────────
  let T_regen, T_friction;
  if (params.friction_calib_enable && brake_demand > 0) {
    const v_kmh = v_kmh_in || (omega_motor * params.r_wheel / params.gear_ratio * 3.6);
    const bpp_pct = brake_demand * 100;
    // 회생 비율 2D LUT 보간
    const regen_ratio = bilinear2D(
      params.fric_regen_lut  || [[0.8]],
      params.fric_brake_bpp_pts || [0, 100],
      params.fric_brake_v_pts   || [0, 130],
      bpp_pct, v_kmh
    );
    // EBD: 전후 제동 분배
    let front_ratio = params.ebd_front_ratio_static || 0.65;
    if (params.ebd_enable) {
      const h   = params.ebd_cg_height_m || 0.55;
      const wb  = params.ebd_wheelbase_m || 2.87;
      const decel_g = brake_demand * 0.8; // 추정 감속도
      front_ratio = Math.min(0.85, (params.ebd_front_ratio_static || 0.65) + decel_g * h / wb * 0.3);
    }
    // ABS 압력 한계 클램핑 (bar → 제동력 비율 환산 근사)
    const abs_clamp = Math.min(1.0, (params.abs_pressure_limit_bar || 120) / 120);
    const T_brake_req = brake_demand * T_regen_max * abs_clamp;
    T_regen   = Math.min(T_regen_max, T_brake_req * regen_ratio);
    T_friction = Math.max(0, T_brake_req - T_regen);
    // EBD 분배 정보를 반환값에 포함
    const P_regen = T_regen * omega_motor;
    return { T_regen, T_friction, P_regen, T_regen_max, P_regen_limit, speed_fade,
             regen_ratio, front_ratio, ebd_active: params.ebd_enable };
  }

  T_regen = Math.min(brake_demand * T_regen_max, T_regen_max);
  // Friction brake for remainder
  const T_brake_req = brake_demand * T_max_m * 0.5;
  T_friction = Math.max(0, T_brake_req - T_regen);
  // Regen power (at motor shaft, before efficiency)
  const P_regen = T_regen * omega_motor;
  return { T_regen, T_friction, P_regen, T_regen_max, P_regen_limit, speed_fade };
}

// ── computeRegenChain ──
/**
 * 회생제동 시 에너지 체인 전체의 손실 분해와 효율을 계산한다.
 *
 * 에너지 흐름: 바퀴 → 기어박스 → 모터(발전) → 인버터(정류) → 배터리(충전)
 *   P_wheel_regen = T_regen × ω / η_dl     (기어박스 손실 역산, 바퀴 흡수 출력)
 *   P_motor_mech  = T_regen × ω             (모터 샤프트 입력)
 *   P_ac_gen      = P_motor_mech − P_cu − P_fe − P_mech_loss  (발전 AC 출력)
 *   P_dc_regen    = P_ac_gen − P_inv_loss   (인버터 정류 후 DC)
 *   P_batt_charge = P_dc_regen − Ri×I²     (배터리 실제 충전량)
 *
 * 듀얼 모터(awd_enable) 시 Cu 손실은 torque_split 비율² 스케일, Fe·기계 손실은 ×2.
 * [BugFix-v17-2] P_wheel_regen 분모에 η_dl 적용으로 η_regen 분모 정확화.
 *
 * @param {number} omega_motor  모터 각속도 [rad/s]
 * @param {number} T_regen      회생 토크 [Nm]
 * @param {number} Id           d축 전류 [A]
 * @param {number} Iq           q축 전류 [A]
 * @param {number} soc          현재 SOC [0, 1]
 * @param {object} params       차량 파라미터
 * @param {number} T_batt       배터리 온도 [°C]
 * @param {number} T_motor      모터 온도 [°C]
 * @returns {object}            에너지 체인 전 필드 (computeEnergyChain 호환)
 */
function computeRegenChain(omega_motor, T_regen, Id, Iq, soc, params, T_batt, T_motor) {
  const Rs = (T_motor !== undefined) ? Rs_T(T_motor, params) : params.Rs;
  const I_rms = Math.sqrt(Id * Id + Iq * Iq);
  // [BugFix-v17-2] P_wheel_regen: 모터 샤프트 출력(T×ω)이 아닌 실제 바퀴 회생 출력으로 수정
  //   기존: T_regen * gear_ratio * (omega_motor / gear_ratio) = T_regen * omega_motor  (= P_motor_mech 와 동일, 실제 휠 출력 아님)
  //   물리: 회생 방향(바퀴→기어박스→모터)에서 P_wheel = P_motor_mech / eta_dl
  //         (기어박스 효율 손실 적용: 바퀴에서 더 많은 에너지를 흡수해야 모터에 P_motor_mech 공급)
  //   수정: T_regen * omega_motor / eta_dl
  //   → eta_regen = P_batt_charge / P_wheel_regen 의 분모 정확화 (기존 ~5% 과대 표시 수정)
  //   → P_motor_mech, P_ac_gen 등 하위 에너지 계산은 그대로 모터 샤프트 기준 유지 (SOC·에너지 영향 없음)
  //   기존 P_gear_out (미사용 dead code 및 방향 역산 오류) 함께 제거
  const eta_dl_regen = params.eta_driveline || 0.95;
  const P_wheel_regen = T_regen * omega_motor / eta_dl_regen; // 실제 바퀴 회생 흡수 출력 [W]
  const P_motor_mech  = T_regen * omega_motor;                // 모터 샤프트 입력 출력 [W] (기어 통과 후)
  // Motor in generation mode: P_ac = P_mech - losses
  const P_cu = 1.5 * Rs * I_rms * I_rms;
  const omega_e = omega_motor * (params.poles / 2);
  const f_e = omega_e / (2 * Math.PI);
  const lpm = (T_motor !== undefined) ? lambda_pm_T(T_motor, params) : params.lambda_pm;
  const B_est = Math.min(1.5, lpm * 2);
  const P_fe = (params.k_h * f_e * Math.pow(B_est, 1.6) + params.k_e * f_e * f_e * B_est * B_est) * 50;
  const P_mech_loss = params.k_m0 + params.k_m1 * omega_motor + params.k_m2 * omega_motor * omega_motor;
  const P_ac_gen = Math.max(0, P_motor_mech - P_cu - P_fe - P_mech_loss);
  // Inverter rectification
  const inv_loss_f = 1 + (params.k_inv_temp || 0) * ((T_motor || 25) - params.T_ref);
  const P_inv_loss_single = (6 * (params.V_f_diode * I_rms * 0.45 + params.R_sw * I_rms * I_rms) +
    6 * params.E_on_off * params.f_sw * (I_rms / 300) * (params.V_dc / 400)) * inv_loss_f;

  // [A35-BUG-M2] 회생 시 듀얼 모터 손실 스케일링 (computeEnergyChain과 동일 원리)
  const _regen_is_dual = (params.motor_config === 'dual') ||
                         (params.awd_enable === true &&
                          params.motor_config !== 'single_rear' &&
                          params.motor_config !== 'single_front');
  let _regen_cu_eff, _regen_fe_eff, _regen_mech_eff, _motor_regen_loss;
  let P_inv_loss;
  if (_regen_is_dual) {
    const _rf  = params.torque_split_front != null ? params.torque_split_front : 0.4;
    const _rr  = 1.0 - _rf;
    const _cu_sc = _rf * _rf + _rr * _rr;
    _regen_cu_eff   = P_cu         * _cu_sc;
    _regen_fe_eff   = P_fe         * 2.0;
    _regen_mech_eff = P_mech_loss  * 2.0;
    _motor_regen_loss = _regen_cu_eff + _regen_fe_eff + _regen_mech_eff;
    // 인버터: 선형(45%)+이차(55%) 근사 + 보조전력 2배
    P_inv_loss = P_inv_loss_single * (0.45 + 0.55 * _cu_sc) + params.inv_aux_power * inv_loss_f;
  } else {
    _regen_cu_eff     = P_cu;
    _regen_fe_eff     = P_fe;
    _regen_mech_eff   = P_mech_loss;
    _motor_regen_loss = P_cu + P_fe + P_mech_loss;
    P_inv_loss        = P_inv_loss_single;
  }
  const P_ac_gen_eff = Math.max(0, P_motor_mech - _motor_regen_loss);
  const P_dc_regen = Math.max(0, P_ac_gen_eff - P_inv_loss);
  // Battery charging
  const Ri = R_int_T(soc, T_batt || 25, params);
  const P_loss_batt = (P_dc_regen > 0) ? Math.pow(P_dc_regen / params.V_dc, 2) * Ri : 0;
  const P_batt_charge = Math.max(0, P_dc_regen - P_loss_batt) * (params.K_regen || 1.0); // [A21] K_regen 보정
  const eta_regen = P_wheel_regen > 10 ? P_batt_charge / P_wheel_regen : 0;
  // [v5-bugfix1] computeEnergyChain 호환 필드 추가 — ...chain spread 시 undefined 방지
  const eta_motor_regen = P_motor_mech > 10 ? P_ac_gen_eff / P_motor_mech * 100 : 85;
  const eta_inv_regen   = P_ac_gen_eff > 10 ? P_dc_regen / P_ac_gen_eff * 100 : 95;
  const eta_batt_regen  = P_dc_regen > 10 ? P_batt_charge / P_dc_regen * 100 : 98;
  const eta_total_regen = P_wheel_regen > 10 ? P_batt_charge / P_wheel_regen * 100 : 0;
  return {
    // 회생 전용 필드
    P_wheel_regen: +(P_wheel_regen / 1000).toFixed(3),
    P_ac_gen: +(P_ac_gen_eff / 1000).toFixed(3),
    P_dc_regen: +(P_dc_regen / 1000).toFixed(3),
    P_batt_charge: +(P_batt_charge / 1000).toFixed(3),
    eta_regen: +(eta_regen * 100).toFixed(1),
    P_inv_loss: +(P_inv_loss / 1000).toFixed(4),
    P_loss_batt_regen: +(P_loss_batt / 1000).toFixed(4),
    // computeEnergyChain 호환 필드 (spread 안전)
    P_wheel: +(P_wheel_regen / 1000).toFixed(3),        // 회생: 바퀴에서 회수
    P_motor_mech: +(P_motor_mech / 1000).toFixed(3),
    P_ac: +(P_ac_gen_eff / 1000).toFixed(3),
    P_dc: +(P_dc_regen / 1000).toFixed(3),
    P_batt_chem: -(+(P_batt_charge / 1000).toFixed(3)), // 음수 = 충전 (명시적 Number 변환)
    P_aux: 0,
    P_loss_batt: +(P_loss_batt / 1000).toFixed(4),
    P_loss_inv: +(P_inv_loss / 1000).toFixed(4),
    P_loss_motor: +(_motor_regen_loss / 1000).toFixed(4),  // [A35-BUG-M2] 듀얼 스케일
    P_loss_gear: 0,
    P_cu: +(_regen_cu_eff   / 1000).toFixed(4),            // [A35-BUG-M2] 분배비 기반
    P_fe: +(_regen_fe_eff   / 1000).toFixed(4),            // [A35-BUG-M2] 2× (듀얼시)
    P_mech_loss: +(_regen_mech_eff / 1000).toFixed(4),     // [A35-BUG-M2] 2× (듀얼시)
    P_aero: 0, P_roll: 0, P_grade: 0,
    eta_batt: +eta_batt_regen.toFixed(1),
    eta_inv: +eta_inv_regen.toFixed(1),
    eta_motor: +eta_motor_regen.toFixed(1),
    eta_gear: +(eta_dl_regen * 100).toFixed(1), // 실제 회생 드라이브라인 효율 반영
    eta_total: +eta_total_regen.toFixed(1),
    // 열 발생 [W] — thermalStep 호환
    Q_batt: P_loss_batt,
    Q_inv:  P_inv_loss,
    Q_motor: P_cu + P_fe + P_mech_loss,
    Q_motor_regen: P_cu + P_fe + P_mech_loss,
    Q_inv_regen: P_inv_loss,
    Q_batt_regen: P_loss_batt,
    // Derate (회생 중에도 표시)
    derate_batt: 100, derate_inv: 100, derate_motor: 100,
    // 기타 호환 필드
    rho_eff: 1.2, Crr_eff: 0.01,
    Rs_eff: 0, lambda_eff: 0, Ri_eff: 0,
  };
}

// ── regenFrictionBlend ──
/**
 * 회생/마찰 토크 블렌딩을 속도·SOC 조건과 S-커브로 조절한다.
 *
 * 블렌딩 인자 = speed_blend × soc_blend → sigmoid(k, 0.5) 적용
 *   · speed_blend: v < v_lo → 0 (순수 마찰), v > v_hi → 1 (순수 회생)
 *   · soc_blend:   SOC > 0.9 → 선형 감소 (배터리 과충전 보호)
 *
 * 저크 제한: dT/dt ≤ regen_max_jerk × m × r_wheel/gear_ratio × dt
 * [Fix-DC2] 저크 제한이 실제로 적용되려면 호출자가 다음 스텝 전에
 *   params._regen_T_prev = result.next_regen_T_prev 로 갱신해야 한다.
 *
 * @param {number} T_regen_raw   원시 회생 토크 [Nm]
 * @param {number} T_fric_raw    원시 마찰 토크 [Nm]
 * @param {number} v             차속 [m/s]
 * @param {number} soc           현재 SOC [0, 1]
 * @param {number} omega_motor   모터 각속도 [rad/s]
 * @param {object} params        차량 파라미터
 * @param {number} dt            시뮬레이션 시간 스텝 [s]
 * @returns {{ T_regen, T_friction, blend_factor, speed_blend, soc_blend, next_regen_T_prev }}
 */
function regenFrictionBlend(T_regen_raw, T_fric_raw, v, soc, omega_motor, params, dt) {
  if (!params.regen_blend_enable) return { T_regen: T_regen_raw, T_friction: T_fric_raw };

  // 속도 fade-out: v < v_lo→ 완전 마찰, v > v_hi → 정상
  const v_lo = params.regen_blend_v_lo || 5.0;
  const v_hi = params.regen_blend_v_hi || 15.0;
  const speed_blend = v < v_lo ? 0 : v > v_hi ? 1 :
    (v - v_lo) / (v_hi - v_lo);

  // SOC 상한 보호: SOC > 0.9에서 선형 감소
  const soc_blend = soc < 0.9 ? 1 : Math.max(0, 1 - (soc - 0.9) / 0.08);

  // S-커브 (sigmoid)
  const k = params.regen_blend_k_nonlin || 2.5;
  const blendFactor = speed_blend * soc_blend;
  const blend = 1 / (1 + Math.exp(-k * (blendFactor - 0.5)));

  const T_regen   = T_regen_raw * blend;
  const T_friction = T_fric_raw + T_regen_raw * (1 - blend);

  // [Fix-DC2] 저크 제한: params._regen_T_prev를 읽기만 하고 갱신하지 않던 버그 수정.
  // params 직접 변이를 피하기 위해 반환값에 next_regen_T_prev를 포함시키고,
  // 호출자가 다음 스텝 전에 params._regen_T_prev = result.next_regen_T_prev 로 갱신해야 함.
  const max_jerk = (params.regen_max_jerk || 3.0) * params.m;
  const dT_max   = max_jerk * params.r_wheel / params.gear_ratio * dt;
  // T_regen_prev가 없으면 첫 스텝으로 간주 (제한 미적용)
  const T_regen_prev = params._regen_T_prev || 0;
  const T_regen_clamped = T_regen_prev > 0
    ? Math.min(T_regen, T_regen_prev + dT_max)
    : T_regen;

  return {
    T_regen:    T_regen_clamped,
    T_friction: Math.min(T_friction + (T_regen - T_regen_clamped), params.friction_brake_max_torque || 800),
    blend_factor: +blendFactor.toFixed(3),
    speed_blend: +speed_blend.toFixed(3),
    soc_blend:   +soc_blend.toFixed(3),
    // [Fix-DC2] 호출자가 다음 스텝 전에 params._regen_T_prev 에 이 값을 써야 저크 제한이 실제로 작동함
    next_regen_T_prev: T_regen_clamped,
  };
}

// ── regenEnergyOptimizer ──
/**
 * 에너지 회수와 승차감(저크) 사이의 가중치 기반 최적 회생/마찰 토크 분배를 계산한다.
 *
 * 목적함수: score = w_E × E_recover − w_J × jerk_cost
 *   w_E = regen_opt_weight_energy (기본 0.7)
 *   w_J = regen_opt_weight_comfort (기본 0.3)
 *
 * 제약:
 *   · P_max_regen + params.arb_chg_limit_kw 가 상한
 *   · SOC ≥ bms_soc_charge_cutoff → soc_factor=0 (과충전 방지)
 *   · T_batt > T_batt_derate_start → T_batt_factor 감소 (열 보호)
 *
 * 탐색: 20분할 균등 격자 검색 (1D 연속 최적화 근사)
 *
 * @param {number} omega_m       모터 각속도 [rad/s]
 * @param {number} v             차속 [m/s]
 * @param {number} soc           현재 SOC [0, 1]
 * @param {number} T_batt        배터리 온도 [°C]
 * @param {number} T_motor       모터 온도 [°C]
 * @param {object} params        차량 파라미터
 * @param {number} brake_demand  제동 수요 [0, 1]
 * @returns {{ T_regen, T_friction, eta_regen, soc_factor, T_batt_factor, regen_ratio, E_recover_rate }}
 */
function regenEnergyOptimizer(omega_m, v, soc, T_batt, T_motor, params, brake_demand) {
  // [A10] Arbitration 충전 한계도 회생 상한에 반영
  const P_regen_arb = (params.arb_chg_limit_kw || 999) * 1000;
  const T_max_regen = Math.min(params.P_max_regen, P_regen_arb) / Math.max(omega_m, 10);
  const soc_limit   = params.bms_soc_charge_cutoff || 0.98;
  const T_batt_lim  = params.T_batt_derate_start || 45;

  // 속도 의존 회생 효율 (저속 → 회생 비효율)
  const v_kmh = v * 3.6;
  const eta_regen = v_kmh < 5  ? 0.3 :
                    v_kmh < 20 ? 0.5 + 0.02 * (v_kmh - 5) : 0.8;

  // SOC 포화 → 회생 축소
  const soc_factor = soc >= soc_limit ? 0 :
                     soc >= soc_limit - 0.05 ? (soc_limit - soc) / 0.05 : 1.0;

  // 온도 패널티 (과열 시 회생 감소)
  const T_batt_factor = T_batt > T_batt_lim ? Math.max(0, 1 - (T_batt - T_batt_lim) / 10) : 1.0;

  // 최대 허용 회생 토크
  const T_regen_limit = T_max_regen * soc_factor * T_batt_factor;

  // 가중치 기반 최적 분배
  const w_E = params.regen_opt_weight_energy  || 0.7;
  const w_J = params.regen_opt_weight_comfort || 0.3;

  // 최적화: 미분 가능 1D 검색 (간소화 황금분할 3점)
  let T_opt = 0, score_max = -Infinity;
  const N_search = 20;
  for (let i = 0; i <= N_search; i++) {
    const T_try = T_regen_limit * i / N_search;
    const E_recover  = eta_regen * T_try * Math.max(omega_m, 1);   // 회수 에너지 (비례)
    const T_friction = Math.max(0, brake_demand * T_max_regen - T_try);
    const jerk_cost  = T_friction * T_friction;                    // 마찰 토크 불연속성 페널티
    const score = w_E * E_recover - w_J * jerk_cost * 0.001;
    if (score > score_max) { score_max = score; T_opt = T_try; }
  }

  const T_friction_opt = Math.max(0, brake_demand * T_max_regen - T_opt);
  const regen_ratio    = T_regen_limit > 0 ? T_opt / T_regen_limit : 0;

  return {
    T_regen:    +T_opt.toFixed(1),
    T_friction: +T_friction_opt.toFixed(1),
    eta_regen:  +eta_regen.toFixed(3),
    soc_factor: +soc_factor.toFixed(3),
    T_batt_factor: +T_batt_factor.toFixed(3),
    regen_ratio:+regen_ratio.toFixed(3),
    E_recover_rate: +(eta_regen * T_opt * Math.max(omega_m, 1) / 1000).toFixed(2),  // kW
  };
}

// ── launchControlTorque ──
/**
 * 발진 제어(Launch Control) 토크 명령을 PI 슬립 제어로 계산한다.
 *
 * 알고리즘:
 *   1) 슬립률: slip = (v_wheel − v) / v_ref
 *   2) 슬립 오차: e_slip = slip − lc_slip_target (기본 0.12)
 *   3) PI 피드백: T_pi = −(Kp × e_slip + Ki × ∫e_slip·dt)
 *   4) 토크 램프: T_ramped = min(T_prev + ramp_rate × dt, T_max) (급격한 슬립 방지)
 *   5) 최종 토크: T_lc = clamp(T_ramped + T_pi, 0, T_ramped)
 *   6) v ≥ lc_deactivate_kmh(기본 30 km/h) 이상이면 null 반환 → 일반 제어로 복귀
 *
 * @param {number} v          차속 [m/s]
 * @param {number} omega_m    모터 각속도 [rad/s]
 * @param {number} mu_hat     추정 노면 마찰계수 (현재 미사용, 향후 적응 제어용)
 * @param {number} soc        현재 SOC [0, 1]
 * @param {object} params     차량 파라미터 (lc_slip_target, lc_slip_kp, lc_slip_ki 등)
 * @param {object} lc_state   이전 스텝 LC 상태 { int_slip, T_cmd_prev }
 * @param {number} dt         시뮬레이션 시간 스텝 [s]
 * @returns {{ T_lc: number|null, new_state, slip, lc_active, e_slip }}
 *   T_lc: 활성 중 토크 명령 [Nm], 비활성이면 null
 */
function launchControlTorque(v, omega_m, mu_hat, soc, params, lc_state, dt) {
  const r_w   = params.r_wheel || 0.33;
  const gr    = params.gear_ratio || 9.5;
  const T_max = params.T_max_motor || 400;
  const v_wheel = omega_m * r_w / gr;  // 바퀴 선속도
  // 슬립률 (v < 0.5 m/s 이하에서 안정화)
  const v_ref  = Math.max(v, 0.5);
  const slip   = v > 0.3 ? (v_wheel - v) / v_ref : 0;

  const slip_tgt = params.lc_slip_target || 0.12;
  const e_slip   = slip - slip_tgt;

  // PI 슬립 제어
  const Kp = params.lc_slip_kp || 3000;
  const Ki = params.lc_slip_ki || 150;
  let int_slip = lc_state.int_slip || 0;
  int_slip = Math.max(-T_max, Math.min(T_max, int_slip + e_slip * dt));

  // 토크 명령 (슬립 과다 시 감소)
  const T_pi = -(Kp * e_slip + Ki * int_slip);

  // 토크 램프 (발차 초기 부드러운 증가)
  const ramp_rate = params.lc_torque_ramp_rate || 800;  // Nm/s
  const T_ramped  = Math.min(lc_state.T_cmd_prev + ramp_rate * dt, T_max);

  // 최종 토크: 슬립 피드백 + 램프 가중
  const lc_active = v < (params.lc_deactivate_kmh || 30) / 3.6;
  const T_lc = lc_active
    ? Math.max(0, Math.min(T_ramped, T_ramped + T_pi))
    : null;  // null → 일반 제어로 복귀

  const new_state = {
    int_slip,
    T_cmd_prev: T_lc !== null ? T_lc : (lc_state.T_cmd_prev || 0),
    slip,
    lc_active,
    e_slip,
  };
  return { T_lc, new_state, slip, lc_active, e_slip };
}

export {
  driveModeThrottle,
  driveModeRegenStrength,
  regenMapDemand,
  regenTorqueEnhanced,
  computeRegenChain,
  regenFrictionBlend,
  regenEnergyOptimizer,
  launchControlTorque,
};